import type { AnalyticsV2QueryParams, MetricBreakdownRow, ScalarMetricResult, SeriesMetricResult } from '@/types/analyticsV2';

/** kim-analytics' Sentinel card keys, all fetched in one /metrics request. */
export const SENTINEL_METRIC_KEYS = [
  'sentinelTotalTickets',
  'sentinelInScope',
  'sentinelOutOfScope',
  'sentinelHandled',
  'sentinelCsat',
  'sentinelCsatResponse',
  'sentinelFirstResponseTime',
  'sentinelResolutionTime',
  'sentinelFirstResponseBusinessTime',
  'sentinelResolutionBusinessTime',
  'sentinelReopenRate',
  'sentinelFtnr',
  'sentinelUnassignment',
  'sentinelUnassignedBySentinel',
  'sentinelUnassignedByHuman',
  'sentinelMissedOpportunity',
  'sentinelAutomation',
  'sentinelAiCoverage',
  'sentinelAiGenerationFailure',
  'sentinelOversightPromptsOneToTwo',
  'sentinelOversightPromptsMoreThanTwo',
  'sentinelAiAccuracy',
  'sentinelCorrectedResponses',
  'sentinelAiAccuracyWithAutomated',
] as const;

/** The keys behind the report's daily series, all fetched in one /metrics/series request. */
export const SENTINEL_SERIES_KEYS = [
  'sentinelTotalTickets',
  'sentinelInScope',
  'sentinelOutOfScope',
  'sentinelMissedOpportunity',
  'sentinelHandled',
  'sentinelAutomation',
  'sentinelUnassignment',
  'sentinelReopenRate',
  'sentinelFtnr',
  'sentinelAiCoverage',
  'sentinelAiGenerationFailure',
  'sentinelAiAccuracy',
  'sentinelCorrectedResponses',
  'sentinelFirstResponseTime',
  'sentinelResolutionTime',
  'sentinelFirstResponseBusinessTime',
  'sentinelResolutionBusinessTime',
] as const;

export type SentinelMetricKey = (typeof SENTINEL_METRIC_KEYS)[number];

/** The keys each table renders, so a table loads only the metric families it shows. */
export const SENTINEL_TABLE_KEYS = {
  tickets: ['sentinelTotalTickets', 'sentinelInScope', 'sentinelOutOfScope', 'sentinelHandled', 'sentinelUnassignment', 'sentinelMissedOpportunity', 'sentinelAutomation'],
  messages: ['sentinelAiCoverage', 'sentinelAiGenerationFailure', 'sentinelAiAccuracy', 'sentinelCorrectedResponses'],
  performance: [
    'sentinelHandled',
    'sentinelAiAccuracy',
    'sentinelAiAccuracyWithAutomated',
    'sentinelCsat',
    'sentinelReopenRate',
    'sentinelFtnr',
    'sentinelFirstResponseTime',
    'sentinelResolutionTime',
    'sentinelFirstResponseBusinessTime',
    'sentinelResolutionBusinessTime',
    'sentinelOversightPromptsOneToTwo',
    'sentinelOversightPromptsMoreThanTwo',
  ],
} as const satisfies Record<string, readonly SentinelMetricKey[]>;
export type SentinelSeriesKey = (typeof SENTINEL_SERIES_KEYS)[number];
export type SentinelMetrics = Record<SentinelMetricKey, ScalarMetricResult>;
export type SentinelSeries = Record<SentinelSeriesKey, SeriesMetricResult>;

/** The ticket dimensions every Sentinel metric can be grouped and filtered by, plus a failed turn's reason. */
export type SentinelDimension = 'category' | 'subcategory' | 'channel' | 'bucket' | 'failureReason';

/** Picked values per dimension; `null` picks the tickets with no value (e.g. no category). */
export type SentinelFilters = Partial<Record<SentinelDimension, (string | null)[]>>;

/** Account, brand, range and filters of a Sentinel Analytics read. */
export type SentinelAnalyticsQuery = AnalyticsV2QueryParams & { filters?: SentinelFilters };

/** One group of a breakdown: its dimension values and each metric over its tickets. */
export type SentinelBreakdownRow = MetricBreakdownRow<SentinelDimension, SentinelMetricKey>;
